import { useCallback, useEffect, useRef, useState } from 'react';
import { usePersistentState } from '../../core/usePersistentState.ts';
import { api, ApiClientError } from '../../core/apiClient.ts';
import { useToast } from '../../core/contexts/ToastContext.tsx';
import { summarizeSyncJobs,syncStatusPollDelay, type SyncJobStatus, type SyncProgress } from './sync-progress.ts';

interface SyncRequest { date_from:string; date_to:string; account_ids?:string[]; intent?:'wide'|'sync'; opened_campaign_ids?:string[]; }

export function useServerStatsSync(onComplete:()=>void) {
  const {showToast}=useToast();
  const [ids,setIds]=usePersistentState<string[]>('stats.sync-jobs',[],(v):v is string[]=>
    Array.isArray(v)&&v.length<=200&&v.every(id=>typeof id==='string'&&/^[-a-zA-Z0-9]{1,80}$/.test(id)));
  const [starting,setStarting]=useState(false);
  const [cancelling,setCancelling]=useState(false);
  const [cancelRequestedState,setCancelRequestedState]=useState(false);
  const [syncProgress,setProgress]=useState<SyncProgress|null>(null);
  const [evaluationIds,setEvaluationIds]=usePersistentState<string[]>('stats.evaluation-jobs',[],(v):v is string[]=>
    Array.isArray(v)&&v.length<=200&&v.every(id=>typeof id==='string'&&/^[-a-zA-Z0-9]{1,80}$/.test(id)));
  const [evaluationPending,setEvaluationPending]=useState<number|null>(null);
  const completedCallback=useRef(onComplete);completedCallback.current=onComplete;
  const cancelRequested=useRef(false);
  useEffect(()=>{
    if(!ids.length){setProgress(null);return;}
    let stopped=false;let timer:ReturnType<typeof setTimeout>|undefined;let notified=false;
    const monitor=async()=>{
      let nextPollMs=30000; // Back off on failed status reads.
      try {
        const {jobs}=await api.get<{jobs:SyncJobStatus[]}>(`/api/ads-stats/sync/server/status?job_ids=${encodeURIComponent(ids.join(','))}`);
        if(stopped)return;
        if(!Array.isArray(jobs)||jobs.length!==ids.length)throw new Error('Частину завдань синхронізації не знайдено');
        const progress=summarizeSyncJobs(jobs);
        nextPollMs=syncStatusPollDelay(progress);
        const {completed,pending,errorSummary}=progress;
        const failures=jobs.filter(j=>j.phase==='failed');
        setProgress(progress);
        if(!pending) {
          const awaiting=jobs.filter(job=>job.evaluationStatus==='pending').map(job=>job.id);
          if(awaiting.length)setEvaluationIds(previous=>[...new Set([...previous,...awaiting])].slice(-200));
          setIds([]);completedCallback.current();
          if(cancelRequested.current){
            cancelRequested.current=false;setCancelRequestedState(false);setCancelling(false);
            showToast(progress.cancelled?`Синхронізацію зупинено. Готово: ${completed}, скасовано: ${progress.cancelled}${failures.length?`, помилок: ${failures.length}`:''}`:`Усі звіти вже завершили публікацію.`,progress.cancelled?'info':'success');
          }else showToast(failures.length?`Синхронізація: ${completed} успішно, ${failures.length} з помилкою. ${errorSummary}`:`Синхронізовано: ${completed} кабінетів`,failures.length?'error':'success');
          return;
        }
      }catch(error) {
        if(stopped)return;
        if(!notified){showToast(error instanceof Error?error.message:'Не вдалося отримати прогрес. Сервер продовжує синхронізацію.','error');notified=true;}
      }
      if(!stopped)timer=setTimeout(monitor,cancelRequested.current?5000:nextPollMs);
    };
    void monitor();
    // Unmount stops UI polling only. The server owns job lifetime and recovery.
    return ()=>{stopped=true;if(timer)clearTimeout(timer);};
  },[ids,setIds,setEvaluationIds,showToast]);
  useEffect(()=>{
    if(!evaluationIds.length)return;
    let stopped=false;let timer:ReturnType<typeof setTimeout>|undefined;
    setEvaluationPending(evaluationIds.length);
    const monitor=async()=>{
      try {
        const {jobs}=await api.get<{jobs:SyncJobStatus[]}>(`/api/ads-stats/sync/server/status?job_ids=${encodeURIComponent(evaluationIds.join(','))}`);
        if(stopped)return;
        if(!Array.isArray(jobs)||jobs.length!==evaluationIds.length)throw new Error('Incomplete evaluation status');
        const pending=jobs.filter(job=>job.evaluationStatus==='pending'||job.evaluationStatus===undefined).length;
        setEvaluationPending(pending);
        if(!pending){setEvaluationIds([]);return;}
      }catch{/* Keep the last known pending state; a failed poll cannot claim completion. */}
      if(!stopped)timer=setTimeout(monitor,30000);
    };
    void monitor();
    return ()=>{stopped=true;if(timer)clearTimeout(timer);};
  },[evaluationIds,setEvaluationIds]);
  const startSync=useCallback(async(body:SyncRequest)=>{
    if(starting||ids.length)return;
    setStarting(true);
    try {
      const result=await api.post<{ok:boolean;jobIds:string[];errors:Array<{ad_account_id:string;error:string}>}>('/api/ads-stats/sync/server/start',body);
      if(!result.ok||!result.jobIds.length)throw new Error(result.errors?.map(e=>`${e.ad_account_id}: ${e.error}`).join('; ')||'Немає доступних кабінетів');
      setIds(result.jobIds);
      setProgress({pending:result.jobIds.length,completed:0,failed:0,cancelled:0,total:result.jobIds.length,queued:result.jobIds.length,waitingMeta:0,downloading:0,publishing:0});
      if(result.errors?.length)showToast(result.errors.map(e=>`${e.ad_account_id}: ${e.error}`).join('; '),'error');
      else showToast('Синхронізацію додано до черги. Можна закрити сторінку.','info');
    }catch(error){showToast(error instanceof ApiClientError||error instanceof Error?error.message:'Помилка запуску синхронізації','error');}
    finally{setStarting(false);}
  },[starting,ids,setIds,showToast]);
  const cancelSync=useCallback(async()=>{
    if(!ids.length||cancelling)return;
    setCancelling(true);cancelRequested.current=true;
    try{
      await api.post<{ok:boolean;accepted:boolean;requested:number}>('/api/ads-stats/sync/server/cancel',{job_ids:ids});
      setCancelRequestedState(true);showToast('Запит на зупинку надіслано. Завершуємо поточний запис і скасовуємо решту кабінетів.','info');
    }catch(error){cancelRequested.current=false;setCancelRequestedState(false);showToast(error instanceof Error?error.message:'Не вдалося зупинити синхронізацію','error');}
    finally{setCancelling(false);if(!cancelRequested.current)setCancelRequestedState(false);}
  },[ids,cancelling,setIds,showToast]);
  return {syncing:starting||ids.length>0,syncProgress,evaluationPending,startSync,cancelSync,cancelling,cancelRequested:cancelRequestedState};
}
